import React, { useState } from 'react';
import type { DreamFormData } from '../types/frontend';

interface DreamFormProps {
  onSubmit: (data: DreamFormData) => void;
  isLoading: boolean;
}

export const DreamForm: React.FC<DreamFormProps> = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState<DreamFormData>({
    dreamText: '',
    context: {
      economicStatus: '',
      socialImportance: '',
      socialPosition: '',
      healthStatus: '',
      concerns: '',
    },
  });

  const [showValidationError, setShowValidationError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.dreamText.trim()) {
      setShowValidationError(false);
      onSubmit(formData);
    } else {
      setShowValidationError(true);
    }
  };

  const updateContext = (key: keyof DreamFormData['context'], value: any) => {
    setFormData(prev => ({
      ...prev,
      context: {
        ...prev.context,
        [key]: value,
      },
    }));
  };

  const loadDemoData = () => {
    setFormData({
      dreamText:
        'I turned into a golden bird and flew over a dark forest. I landed near a temple and ate sacred bread. A priest gave me a silver cup filled with water.',
      context: {
        economicStatus: 'poor', // Trigger "if you are poor" rules
        socialImportance: 'ordinary',
        socialPosition: 'established',
        healthStatus: 'healthy',
        concerns: 'Concerned about an important business decision',
      },
    });
  };

  return (
    <form className="dream-form" data-testid="dream-form" onSubmit={handleSubmit}>
      {/* Validation Error */}
      {showValidationError && (
        <div
          data-testid="error-message"
          style={{
            background: 'rgba(139, 74, 58, 0.08)',
            border: '1px solid var(--omen-unfavorable)',
            padding: 'var(--space-3)',
            marginBottom: 'var(--space-5)',
            color: 'var(--omen-unfavorable)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Dream text is required. Please describe your dream before submitting.
        </div>
      )}

      {/* Main Dream Text */}
      <div className="form-section">
        <label htmlFor="dreamText" className="form-label">
          Your Dream
        </label>
        <textarea
          id="dreamText"
          data-testid="dream-text"
          className="form-textarea"
          value={formData.dreamText}
          onChange={e => setFormData(prev => ({ ...prev, dreamText: e.target.value }))}
          placeholder="Describe in complete sentences. Example: I walked through a temple made of cedar wood. The walls were lined with lapis lazuli..."
          required
        />
      </div>

      {/* Collapsible Context Section */}
      <details style={{ marginBottom: 'var(--space-6)' }}>
        <summary
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 500,
            color: 'var(--soot)',
            cursor: 'pointer',
            userSelect: 'none',
            marginBottom: 'var(--space-4)',
            listStyle: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <span style={{ color: 'var(--dust)', fontSize: '1.2rem' }}>▶</span>
          Additional context (optional)
        </summary>

        <div className="form-grid" style={{ paddingLeft: 'var(--space-6)' }}>
          <div className="form-section">
            <label className="form-label" htmlFor="economic">
              Economic Status
            </label>
            <select
              id="economic"
              data-testid="economic-status"
              className="form-select"
              value={formData.context.economicStatus}
              onChange={e => updateContext('economicStatus', e.target.value)}
            >
              <option value="">Not specified</option>
              <option value="poor">Poor</option>
              <option value="rich">Rich</option>
            </select>
          </div>

          <div className="form-section">
            <label className="form-label" htmlFor="importance">
              Social Importance
            </label>
            <select
              id="importance"
              data-testid="social-importance"
              className="form-select"
              value={formData.context.socialImportance}
              onChange={e => updateContext('socialImportance', e.target.value)}
            >
              <option value="">Not specified</option>
              <option value="important">Important person</option>
              <option value="ordinary">Ordinary person</option>
            </select>
          </div>
        </div>

        <div className="form-grid" style={{ paddingLeft: 'var(--space-6)' }}>
          <div className="form-section">
            <label className="form-label" htmlFor="position">
              Social Position
            </label>
            <select
              id="position"
              className="form-select"
              value={formData.context.socialPosition}
              onChange={e => updateContext('socialPosition', e.target.value)}
            >
              <option value="">Not specified</option>
              <option value="established">Well-established</option>
              <option value="newcomer">Newcomer/Unestablished</option>
            </select>
          </div>

          <div className="form-section">
            <label className="form-label" htmlFor="health">
              Health Status
            </label>
            <select
              id="health"
              data-testid="health-status"
              className="form-select"
              value={formData.context.healthStatus}
              onChange={e => updateContext('healthStatus', e.target.value)}
            >
              <option value="">Not specified</option>
              <option value="sick">Sick/Unwell</option>
              <option value="healthy">Healthy/Well</option>
            </select>
          </div>
        </div>

        <div className="form-section" style={{ paddingLeft: 'var(--space-6)' }}>
          <label className="form-label" htmlFor="concerns">
            Current Concerns
          </label>
          <textarea
            id="concerns"
            data-testid="concerns"
            className="form-textarea"
            style={{ minHeight: '80px' }}
            value={formData.context.concerns}
            onChange={e => updateContext('concerns', e.target.value)}
            placeholder="What's weighing on your mind?"
          />
        </div>
      </details>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexDirection: 'column' }}>
        <button
          type="submit"
          data-testid="submit-dream"
          className="submit-button"
          disabled={isLoading || !formData.dreamText.trim()}
        >
          {isLoading ? 'Analyzing Dream...' : 'Interpret My Dream'}
        </button>

        <button
          type="button"
          onClick={loadDemoData}
          disabled={isLoading}
          style={{
            background: 'transparent',
            border: '1px solid var(--stone-dark)',
            color: 'var(--dust)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: '4px',
            fontSize: 'var(--text-sm)',
            fontWeight: 400,
            cursor: 'pointer',
            transition: 'background 0.2s ease, color 0.2s ease',
          }}
          onMouseEnter={e => {
            if (!isLoading) {
              e.currentTarget.style.background = 'var(--stone-light)';
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          Try example dream
        </button>
      </div>
    </form>
  );
};
